import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { EpisodeCard, ArticleCard, MemberCard } from "../components/Cards";
import { ArrowRight } from "lucide-react";

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/0e9e29b6-7cdf-4639-a6dd-82d2738d34a7/images/76256d80ec994dbd221d7219118d2db21b4a77e6ec3ba0b67048b76e5ea91f43.png";

export default function Home() {
  const [episodes, setEpisodes] = useState([]);
  const [articles, setArticles] = useState([]);
  const [members, setMembers] = useState([]);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [epRes, feRes, arRes, meRes] = await Promise.all([
          api.get("/episodes", { params: { limit: 6 } }),
          api.get("/episodes", { params: { featured: true, limit: 1 } }),
          api.get("/articles", { params: { limit: 4 } }),
          api.get("/members", { params: { limit: 6 } }),
        ]);
        setEpisodes(epRes.data);
        setFeatured(feRes.data[0] || null);
        setArticles(arRes.data);
        setMembers(meRes.data);
      } catch (e) {
        console.error("Home load error", e);
      }
    })();
  }, []);

  return (
    <div data-testid="home-page" className="relative">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={featured?.cover_image || HERO_IMAGE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, var(--bg) 0%, rgba(9,10,12,0.6) 40%, rgba(9,10,12,0.2) 80%)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(9,10,12,0.85) 0%, rgba(9,10,12,0.3) 60%, transparent 100%)" }} />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 pt-32 w-full">
          <div className="max-w-3xl reveal">
            <p className="eyebrow mb-6">Wealth First Inc. · Est. MMXXVI</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[5.5rem] mb-8" style={{ color: "var(--ink)" }}>
              The lives of those who quietly <em style={{ color: "var(--brass)", fontStyle: "italic" }}>shape</em> the world.
            </h1>
            <p className="text-lg md:text-xl leading-relaxed max-w-2xl mb-10" style={{ color: "var(--ink-muted)" }}>
              A subscription television and editorial archive. Long-form interviews with the magnates, family-office stewards,
              Fortune Top 100 CEOs, and investment managers who shape modern affluence — on the record, in their own words.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/episodes" data-testid="hero-watch-cta" className="btn-brass">
                Watch Interviews <ArrowRight size={14} />
              </Link>
              <Link to="/subscribe" data-testid="hero-subscribe-cta" className="btn-ghost">
                Become a Member
              </Link>
            </div>
          </div>

          {featured && (
            <div className="mt-20 max-w-md reveal reveal-delay-2" style={{ borderLeft: "1px solid var(--brass)", paddingLeft: "1.5rem" }}>
              <p className="eyebrow mb-2">Now Featured</p>
              <Link to={`/episodes/${featured.id}`} className="font-display text-2xl block hover:text-[var(--brass)] transition-colors">
                {featured.title}
              </Link>
              <p className="text-sm mt-1" style={{ color: "var(--ink-muted)" }}>
                With {featured.guest_name} · {featured.runtime}
              </p>
            </div>
          )}
        </div>

        {/* Bottom hairline */}
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: "var(--hairline)" }} />
      </section>

      {/* INTERVIEWS GRID */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="eyebrow mb-3">The Library</p>
            <h2 className="font-display text-5xl md:text-6xl">Recent Interviews</h2>
          </div>
          <Link to="/episodes" className="text-sm uppercase tracking-[0.22em] brass-link">
            View All <ArrowRight size={12} className="inline ml-2" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {episodes.slice(0, 6).map((ep) => (
            <EpisodeCard key={ep.id} ep={ep} />
          ))}
        </div>
      </section>

      <hr className="hairline max-w-[1400px] mx-auto" />

      {/* EDITORIAL */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-3">Editorial</p>
            <h2 className="font-display text-5xl md:text-6xl mb-6">
              Reportage <br/>from <em style={{ color: "var(--brass)" }}>quieter</em> rooms.
            </h2>
            <p className="text-base leading-relaxed mb-8" style={{ color: "var(--ink-muted)" }}>
              Long-form essays, reported pieces, and profiles. Written slowly. Edited with care.
              Published only when finished.
            </p>
            <Link to="/articles" className="btn-ghost" data-testid="editorial-cta">All Editorial</Link>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-10">
            {articles.slice(0, 4).map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </div>
      </section>

      <hr className="hairline max-w-[1400px] mx-auto" />

      {/* DIRECTORY */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="eyebrow mb-3">The Directory</p>
            <h2 className="font-display text-5xl md:text-6xl">Profiled Members</h2>
          </div>
          <Link to="/directory" className="text-sm uppercase tracking-[0.22em] brass-link">
            Browse Directory <ArrowRight size={12} className="inline ml-2" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {members.slice(0, 4).map((m) => (
            <MemberCard key={m.id} member={m} />
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="relative overflow-hidden p-10 lg:p-20" style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="eyebrow mb-4">Membership</p>
              <h3 className="font-display text-4xl md:text-5xl mb-6">
                Access the full archive. <br/>
                <em style={{ color: "var(--brass)" }}>One subscription.</em>
              </h3>
              <p className="text-base leading-relaxed max-w-lg" style={{ color: "var(--ink-muted)" }}>
                Three tiers. Unlimited interviews. The complete editorial archive. A directory that grows with every issue.
              </p>
            </div>
            <div className="flex lg:justify-end">
              <Link to="/subscribe" data-testid="cta-strip-subscribe" className="btn-brass">
                See Membership Tiers <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
